import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, ReplaySubject, share } from 'rxjs'

export type SiemaActionListener = BehaviorSubject<SiemaAction | null>

export interface SiemaAction {
  selector?: string | HTMLElement
  item?: HTMLElement
  currentSlide: number
  listener: SiemaActionListener
}

export interface DestroySiemaAction {
  listener: BehaviorSubject<DestroySiemaAction | null>
  selector?: string | HTMLElement
}

export interface SiemaOnNext {
  listener: SiemaActionListener
  selector?: string | HTMLElement
  item?: HTMLElement
  numbers: number
}

export interface SiemaOnPrev {
  listener: SiemaActionListener
  selector?: string | HTMLElement
  item?: HTMLElement
  numbers: number
}

export interface SiemaOnGoTo {
  listener: SiemaActionListener
  selector?: string | HTMLElement
  item?: HTMLElement
  index: number
}

export interface SiemaOnRemove {
  listener: SiemaActionListener
  selector?: string | HTMLElement
  item?: HTMLElement
  index: number
}

export interface SiemaOnInsert {
  listener: SiemaActionListener
  selector?: string | HTMLElement
  item: HTMLElement
  index: number
}

export interface SiemaOnPrepend {
  listener: SiemaActionListener
  selector?: string | HTMLElement
  item: HTMLElement
}

export interface SiemaOnAppend {
  listener: SiemaActionListener
  selector?: string | HTMLElement
  item: HTMLElement
}

export interface SiemaOnDestroy {
  selector?: string | HTMLElement
  listener: BehaviorSubject<DestroySiemaAction | null>
  restoreMarkup: boolean
}

export interface SiemaOnCurrentSlide {
  selector?: string | HTMLElement
  index: number
  listener: SiemaActionListener
}

const next$ = new ReplaySubject<SiemaOnNext>(1)
const prev$ = new ReplaySubject<SiemaOnPrev>(1)
const goTo$ = new ReplaySubject<SiemaOnGoTo>(1)
const remove$ = new ReplaySubject<SiemaOnRemove>(1)
const insert$ = new ReplaySubject<SiemaOnInsert>(1)
const prepend$ = new ReplaySubject<SiemaOnPrepend>(1)
const append$ = new ReplaySubject<SiemaOnAppend>(1)
const destroy$ = new ReplaySubject<SiemaOnDestroy>(1)
const currentSlide$ = new ReplaySubject<SiemaOnCurrentSlide>(1)

@Injectable()
export class CarouselService {
  public next(numbers: number = 1, selector?: string | HTMLElement): Observable<SiemaAction | null> {
    const listener = new BehaviorSubject<SiemaAction | null>(null)
    next$.next({ selector, numbers, listener })
    return listener.asObservable()
  }

  public prev(numbers: number = 1, selector?: string | HTMLElement): Observable<SiemaAction | null> {
    const listener = new BehaviorSubject<SiemaAction | null>(null)
    prev$.next({ selector, numbers, listener })
    return listener.asObservable()
  }

  public goTo(index: number, selector?: string | HTMLElement): Observable<SiemaAction | null> {
    const listener = new BehaviorSubject<SiemaAction | null>(null)
    goTo$.next({ selector, index, listener })
    return listener.asObservable()
  }

  public remove(index: number, selector?: string | HTMLElement): Observable<SiemaAction | null> {
    const listener = new BehaviorSubject<SiemaAction | null>(null)
    remove$.next({ selector, index, listener })
    return listener.asObservable()
  }

  public insert(item: HTMLElement, index: number, selector?: string | HTMLElement): Observable<SiemaAction | null> {
    const listener = new BehaviorSubject<SiemaAction | null>(null)
    insert$.next({ selector, item, index, listener })
    return listener.asObservable()
  }

  public prepend(item: HTMLElement, selector?: string | HTMLElement): Observable<SiemaAction | null> {
    const listener = new BehaviorSubject<SiemaAction | null>(null)
    prepend$.next({ selector, item, listener })
    return listener.asObservable()
  }

  public append(item: HTMLElement, selector?: string | HTMLElement): Observable<SiemaAction | null> {
    const listener = new BehaviorSubject<SiemaAction | null>(null)
    append$.next({ selector, item, listener })
    return listener.asObservable()
  }

  public destroy(selector: string): Observable<DestroySiemaAction | null> {
    const listener = new BehaviorSubject<DestroySiemaAction | null>(null)
    destroy$.next({ selector, listener, restoreMarkup: true })
    return listener.asObservable()
  }

  public currentSlide(index: number, selector?: string | HTMLElement): Observable<SiemaAction | null> {
    const listener = new BehaviorSubject<SiemaAction | null>(null)
    currentSlide$.next({ selector, index, listener })
    return listener.asObservable()
  }

  public onNext(): Observable<SiemaOnNext> {
    return next$.pipe(share())
  }

  public onPrev(): Observable<SiemaOnPrev> {
    return prev$.pipe(share())
  }

  public onGoTo(): Observable<SiemaOnGoTo> {
    return goTo$.pipe(share())
  }

  public onRemove(): Observable<SiemaOnRemove> {
    return remove$.pipe(share())
  }

  public onInsert(): Observable<SiemaOnInsert> {
    return insert$.pipe(share())
  }

  public onPrepend(): Observable<SiemaOnPrepend> {
    return prepend$.pipe(share())
  }

  public onAppend(): Observable<SiemaOnAppend> {
    return append$.pipe(share())
  }

  public onDestroy(): Observable<SiemaOnDestroy> {
    return destroy$.pipe(share())
  }

  public onCurrentSlide(): Observable<SiemaOnCurrentSlide> {
    return currentSlide$.pipe(share())
  }
}
